import Button from "@app/components/button";
import Checkbox from "@app/components/checkbox";
import CustomEditor from "@app/components/custom-editor";
import CustomReactSelect from "@app/components/custom-react-select";
import FileInput from "@app/components/file-input";
import { FormControl } from "@app/components/form-control";
import Input from "@app/components/input";
import TextArea from "@app/components/textarea";
import { BUCKET_URL_KEY } from "@app/constants";
import { BookElement, BookElementChildren, ModelTrain } from "@app/types";
import { Images } from "@assets/images";
import StorageService from "@core/services/storage";
import { FieldArray, useFormikContext } from "formik";
import { ChangeEvent, useRef } from "react";
import { RiCloseLine } from "react-icons/ri";
import { EndpointConversion } from "@app/types/voice-convert.type";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
interface SubChapterProps {
  arrayName: string;
  parentPath: string;
  childElements: BookElementChildren[];
  level: number;
  maxLevel: number;
  parentNumber: string;
  modelTrains?: ModelTrain[];
  endpointConversions?: EndpointConversion[];
  isUpdate?: boolean;
  canCreateAudio?: boolean;
  canUploadAudio?: boolean;
  onAudioChange: (
    event: ChangeEvent<HTMLInputElement>,
    parentPath: string,
    childIndex: number,
    audioIndex: number
  ) => void;
  onThumbnailChange: (name: string, e: ChangeEvent<HTMLInputElement>) => void;
  onPreviewAudio: (chapterName: string, linkAudio?: string) => void;
  expandedState: { [key: string]: boolean };
  setExpandedState: React.Dispatch<
    React.SetStateAction<{ [key: string]: boolean }>
  >;
}

function SubChapter({
  arrayName,
  parentPath,
  childElements,
  level,
  maxLevel,
  parentNumber,
  modelTrains,
  endpointConversions,
  isUpdate,
  canCreateAudio = false,
  canUploadAudio = false,
  onAudioChange,
  onThumbnailChange,
  onPreviewAudio,
  expandedState,
  setExpandedState,
}: SubChapterProps) {
  const formik = useFormikContext<{ elements: BookElement[] }>();
  const s3Url = StorageService.getObject(BUCKET_URL_KEY);
  const inputAudioSubChapterRef = useRef<{ [key: string]: HTMLInputElement }>({});

  const onAddAudioSubChapterClick = (childPath: string, audioIndex: number) => {
    const refKey = `${childPath}-${audioIndex}`;

    if (inputAudioSubChapterRef.current[refKey]) {
      inputAudioSubChapterRef.current[refKey].click();
    }
  };

  const handleAccordionChange =
    (index: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      const key = `${parentPath}.${index}`;

      setExpandedState((prev) => ({
        ...prev,
        [key]: isExpanded,
      }));
    };

  const getLevelColors = (level: number) => {
    const colors = [
      { bg: "#f8fafc", text: "#374151", badge: "bg-green-100 text-green-800" },
      {
        bg: "#f1f5f9",
        text: "#4b5563",
        badge: "bg-purple-100 text-purple-800",
      },
      {
        bg: "#f0f4f8",
        text: "#6b7280",
        badge: "bg-orange-100 text-orange-800",
      },
      { bg: "#fef7f0", text: "#9ca3af", badge: "bg-pink-100 text-pink-800" },
    ];

    return colors[level % colors.length];
  };

  const getLevelMargin = (level: number) => {
    return level * 16; // 16px per level
  };

  return (
    <div style={{ marginLeft: `${getLevelMargin(level)}px` }}>
      {childElements.map((child, childIndex) => {
        const key = `${parentPath}.${childIndex}`;
        const isExpanded = expandedState[key] || false;
        const colors = getLevelColors(level);
        const childPath = `${parentPath}.${childIndex}`;
        const currentNumber = `${parentNumber}.${childIndex + 1}`;

        return (
          <Accordion
            key={key}
            expanded={isExpanded}
            onChange={handleAccordionChange(childIndex)}
            sx={{
              marginY: 1,
              boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
              "&:before": {
                display: "none",
              },
              "&.Mui-expanded": {
                margin: "8px 0",
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: colors.bg,
                minHeight: "48px",
                "&.Mui-expanded": {
                  minHeight: "48px",
                },
                "& .MuiAccordionSummary-content": {
                  alignItems: "center",
                  "&.Mui-expanded": {
                    margin: "12px 0",
                  },
                },
              }}
            >
              <div className="flex items-center justify-between w-full pr-4">
                <div className="flex items-center gap-2">
                  <div
                    className={`${colors.badge} px-2 py-1 rounded-full text-xs font-semibold`}
                  >
                    {currentNumber}
                  </div>
                  <Typography
                    variant="subtitle1"
                    className="font-medium"
                    style={{ color: colors.text }}
                  >
                    {child.name || `Chương ${currentNumber}`}
                  </Typography>
                </div>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    const currentChildren = [...childElements];

                    currentChildren.splice(childIndex, 1);
                    formik.setFieldValue(parentPath, currentChildren);
                  }}
                  size="small"
                  className="text-red-500"
                >
                  <RiCloseLine className="text-[20px]" />
                </IconButton>
              </div>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                padding: "16px",
                backgroundColor: colors.bg,
              }}
            >
              {isExpanded && (
                <>
                  {/* Model Converts */}
                  {!!child.modelConverts?.length && (
                    <div className="flex gap-4 w-full mt-4">
                      <div className="flex-1">
                        <h3 className="font-medium text-[16px] mb-2">
                          Giọng chuẩn
                        </h3>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium text-[16px] mb-2">
                          Giọng đã huấn luyện
                        </h3>
                      </div>
                      <div className="w-[24px]"></div>
                    </div>
                  )}

                  <div className="flex flex-col gap-4 mb-4">
                    {(child.modelConverts || []).map((_, modelIndex) => (
                      <div
                        key={`model-convert-${modelIndex}`}
                        className="flex gap-4 w-full items-center"
                      >
                        <div className="flex-1">
                          <FormControl
                            name={`${childPath}.modelConverts[${modelIndex}].endpoint`}
                          >
                            <CustomReactSelect
                              options={endpointConversions ?? []}
                              placeholder="Các giọng đọc AI phổ biến"
                              isClearable
                              isDisabled={
                                !!child.modelConverts?.[modelIndex]?.model
                                  ?.value
                              }
                            />
                          </FormControl>
                        </div>
                        {child.modelConverts[modelIndex].endpoint?.value && (
                          <div className="flex items-center">
                            <div>
                              <abbr title="Nghe audio">
                                <img
                                  className="w-8 cursor-pointer"
                                  src={Images.PlayPreviewIcon.default}
                                  alt=""
                                  onClick={() => {
                                    const selectedEndpointValue =
                                      child.modelConverts?.[modelIndex]
                                        ?.endpoint?.value;
                                    const selectedEndpoint =
                                      endpointConversions?.find(
                                        (ep) =>
                                          ep.value == selectedEndpointValue
                                      );
                                    const audioLink =
                                      selectedEndpoint?.linkAudio;

                                    onPreviewAudio(
                                      child.name,
                                      audioLink ? s3Url + audioLink : undefined
                                    );
                                  }}
                                />
                              </abbr>
                            </div>
                          </div>
                        )}
                        <div className="flex-1 flex gap-1 items-center">
                          <div className="flex-1">
                            <FormControl
                              name={`${childPath}.modelConverts[${modelIndex}].model`}
                            >
                              <CustomReactSelect
                                options={modelTrains ?? []}
                                placeholder="Các giọng đọc đã tùy chỉnh"
                                isClearable
                                isDisabled={
                                  !!child.modelConverts?.[modelIndex]?.endpoint
                                    ?.value
                                }
                              />
                            </FormControl>
                          </div>
                          {child.modelConverts[modelIndex].model?.value && (
                            <div className="flex items-center">
                              <div>
                                <abbr title="Nghe audio">
                                  <img
                                    className="w-8 cursor-pointer"
                                    src={Images.PlayPreviewIcon.default}
                                    alt=""
                                    onClick={() =>
                                      onPreviewAudio(
                                        child.name,
                                        s3Url +
                                          (
                                            child.modelConverts?.[modelIndex]
                                              ?.model as any
                                          )?.linkAudio
                                      )
                                    }
                                  />
                                </abbr>
                              </div>
                            </div>
                          )}
                        </div>
                        <div
                          className="cursor-pointer flex items-center"
                          onClick={() => {
                            const newModelConverts = [
                              ...(child.modelConverts || []),
                            ];

                            newModelConverts.splice(modelIndex, 1);
                            formik.setFieldValue(
                              `${childPath}.modelConverts`,
                              newModelConverts
                            );
                          }}
                        >
                          <RiCloseLine className="text-[#dc3545] text-[24px] font-semibold" />
                        </div>
                      </div>
                    ))}
                  </div>

                  {canCreateAudio && (
                    <div className="mb-4">
                      <Button
                        type="button"
                        label="Thêm giọng đọc AI tự động từ văn bản"
                        width={350}
                        className="h-[52px] text-[#5570F1] rounded-lg border-[2px] border-solid border-[#5570F1] hover:bg-[#c3cdff]"
                        labelClassName="font-medium pl-4"
                        theme="none"
                        icon={Images.Plus2Icon.default}
                        onClick={() => {
                          const currentModelConverts = child.modelConverts || [];
                          const newModelConverts = [
                            ...currentModelConverts,
                            {
                              endpoint: { value: "", label: "" },
                              model: { value: "", label: "" },
                            },
                          ];

                          formik.setFieldValue(
                            `${childPath}.modelConverts`,
                            newModelConverts
                          );
                        }}
                      />
                      <p className="text-[12px] text-[#dc3545] mt-2">
                        Vui lòng chỉ chọn giọng chuẩn hoặc giọng đã huấn luyện
                      </p>
                    </div>
                  )}

                  {/* Audio */}
                  <div className="flex flex-col gap-4 mb-4">
                    {(child.audios || []).map((audio, audioIndex) => (
                      <div
                        key={`audio-${audioIndex}`}
                        className="flex gap-4 w-full items-center"
                      >
                        <div className="flex-1">
                          <FormControl
                            name={`${childPath}.audios[${audioIndex}].name`}
                          >
                            <Input
                              placeholder="Tên audio"
                              value={audio.name}
                              disabled={!canUploadAudio}
                            />
                          </FormControl>
                        </div>
                        <div className="flex-1 flex gap-1 items-center">
                          <FormControl
                            name={`${childPath}.audios[${audioIndex}].audio`}
                          >
                            <Input
                              placeholder="Audio"
                              inputClassName="!pl-4 !pr-10"
                              inputIcon={
                                canUploadAudio
                                  ? Images.LinkIcon.default
                                  : undefined
                              }
                              inputIconClassName={
                                canUploadAudio
                                  ? "!right-3 !top-[14px] !left-auto !w-6 !h-6 cursor-pointer hover:bg-gray-300"
                                  : undefined
                              }
                              onIconClick={
                                canUploadAudio
                                  ? () =>
                                      onAddAudioSubChapterClick(
                                        childPath,
                                        audioIndex
                                      )
                                  : undefined
                              }
                              disabled
                              value={audio.audio || ""}
                            />
                          </FormControl>
                          {!!audio.audio && (
                            <div>
                              <abbr title="Nghe audio">
                                <img
                                  className="w-8 cursor-pointer"
                                  src={Images.PlayPreviewIcon.default}
                                  alt=""
                                  onClick={() =>
                                    onPreviewAudio(
                                      child.name,
                                      s3Url + audio.audio
                                    )
                                  }
                                />
                              </abbr>
                            </div>
                          )}
                        </div>
                        {canUploadAudio && (
                          <>
                            <div
                              className="cursor-pointer"
                              onClick={() => {
                                const newAudios = [...(child.audios || [])];

                                newAudios.splice(audioIndex, 1);
                                formik.setFieldValue(
                                  `${childPath}.audios`,
                                  newAudios
                                );
                              }}
                            >
                              <RiCloseLine className="text-[#dc3545] text-[24px] font-semibold" />
                            </div>
                            <input
                              ref={(el) => {
                                if (el) {
                                  const refKey = `${childPath}-${audioIndex}`;

                                  inputAudioSubChapterRef.current[refKey] = el;
                                }
                              }}
                              type="file"
                              hidden
                              name={`audio-subchapter-${childPath}-${audioIndex}`}
                              onChange={(e) =>
                                onAudioChange(
                                  e,
                                  parentPath,
                                  childIndex,
                                  audioIndex
                                )
                              }
                            />
                          </>
                        )}
                      </div>
                    ))}
                  </div>

                  {canUploadAudio && (
                    <div className="mb-4">
                      <Button
                        type="button"
                        label="Thêm file Audio có sẵn"
                        width={250}
                        className="h-[52px] text-[#5570F1] rounded-lg border-[2px] border-solid border-[#5570F1] hover:bg-[#c3cdff]"
                        labelClassName="font-medium pl-4"
                        theme="none"
                        icon={Images.Plus2Icon.default}
                        onClick={() => {
                          const currentAudios = child.audios || [];
                          const newAudios = [
                            ...currentAudios,
                            {
                              name: "",
                              audio: "",
                            },
                          ];

                          formik.setFieldValue(`${childPath}.audios`, newAudios);
                        }}
                      />
                    </div>
                  )}

                  {/* Force update audio checkbox */}
                  <div className="mb-3">
                    {isUpdate && (
                      <label htmlFor="">
                        <div className="flex">
                          <FormControl name={`${childPath}.isForceUpdateAudio`}>
                            <Checkbox
                              label="Tạo lại audio"
                              labelClassName="text-sm text-[#83898C]"
                            />
                          </FormControl>
                        </div>
                      </label>
                    )}
                  </div>

                  {/* Basic fields */}
                  <div className="mb-3">
                    <FormControl name={`${childPath}.name`}>
                      <Input placeholder="Tên chương" />
                    </FormControl>
                  </div>
                  <FormControl name={`${childPath}.point`}>
                    <Input placeholder="Giá bán lẻ chương này (Ví dụ: 1.000 Kas)" />
                  </FormControl>

                  {/* Auto generate thumbnail */}
                  {/* <label htmlFor="">
                    <div className="mt-3 flex">
                      <FormControl
                        name={`${childPath}.isAutoGenerateThumbnail`}
                      >
                        <Checkbox
                          label="Tạo hình thu nhỏ bằng AI"
                          labelClassName="text-sm text-[#83898C]"
                        />
                      </FormControl>
                    </div>
                  </label> */}

                  {/* Thumbnail upload */}
                  <div className="mt-3">
                    <FormControl name={`${childPath}.filenames`}>
                      <FileInput
                        label="Chọn hình ảnh cho chương này (JPG, PNG, tỷ lệ 9:16 - Kích thước 600-900px)"
                        labelClassName="text-sm text-[#83898C]"
                        isMultiple
                        onChange={(e) =>
                          onThumbnailChange(`${childPath}.thumbnails`, e)
                        }
                        key={`file-input-${childPath}-${child.thumbnails?.length || 0}`}
                      />
                    </FormControl>
                  </div>

                  {/* Current thumbnails */}
                  {!!child.thumbnails?.length && (
                    <div className="mt-3 flex gap-2 flex-wrap">
                      {(child.thumbnails || []).map((thumbnail, thumbIndex) => (
                        <div
                          key={`${thumbnail}-${thumbIndex}`}
                          className="relative w-32"
                        >
                          <img
                            src={String(s3Url + thumbnail)}
                            className="w-32"
                          />
                          <div
                            className="absolute top-1 right-1 p-1 bg-white rounded-full cursor-pointer hover:bg-gray-100"
                            onClick={() => {
                              const newThumbnails = [
                                ...(child.thumbnails || []),
                              ];

                              newThumbnails.splice(thumbIndex, 1);
                              formik.setFieldValue(
                                `${childPath}.thumbnails`,
                                newThumbnails
                              );
                            }}
                          >
                            <RiCloseLine className="text-[#dc3545] text-[24px] font-semibold" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Description */}
                  <FormControl name={`${childPath}.description`}>
                    <TextArea
                      textAreaClassName="bg-color border-none outline-none rounded-lg !px-4 !py-3"
                      className="mt-3"
                      width="100%"
                      height="auto"
                      rows={5}
                      placeholder="Mô tả"
                      isStandardizeContent={true}
                    />
                  </FormControl>

                  {/* Content */}
                  <div className="mt-3">
                    <FormControl name={`${childPath}.content`}>
                      <CustomEditor
                        placeholder="Nội dung sách"
                        className="mb-7"
                        isStandardizeContent={true}
                      />
                    </FormControl>
                  </div>

                  {/* Recursive children */}
                  {level < maxLevel &&
                    <SubChapter
                      arrayName={arrayName}
                      parentPath={`${childPath}.children`}
                      childElements={child.children ?? []}
                      level={level + 1}
                      maxLevel={maxLevel}
                      parentNumber={currentNumber}
                      modelTrains={modelTrains}
                      endpointConversions={endpointConversions}
                      isUpdate={isUpdate}
                      canCreateAudio={canCreateAudio}
                      canUploadAudio={canUploadAudio}
                      onAudioChange={onAudioChange}
                      onThumbnailChange={onThumbnailChange}
                      onPreviewAudio={onPreviewAudio}
                      expandedState={expandedState}
                      setExpandedState={setExpandedState}
                    />
                  }
                </>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}

      {/* Add chapter button outside accordion */}
      {level < maxLevel && (
        <FieldArray name={parentPath}>
          {({ push: pushChildren }) => (
            <div
              className="mt-4"
              style={{ marginLeft: `${getLevelMargin(level)}px` }}
            >
              <Button
                type="button"
                label={`Thêm chương ${parentNumber}.X`}
                width={212}
                className="h-[52px] text-[#5570F1] rounded-lg border-[2px] border-solid border-[#5570F1] hover:bg-[#c3cdff]"
                labelClassName="font-medium pl-4"
                theme="none"
                icon={Images.Plus2Icon.default}
                onClick={() => {
                  const newChild = {
                    name: "",
                    audio: "",
                    description: "",
                    content: "",
                    point: "0",
                    isForceUpdateAudio: false,
                    isAutoGenerateThumbnail: false,
                    filenames: "",
                    thumbnails: [],
                    children: [],
                    modelConverts: [],
                    audios: [],
                  };

                  pushChildren(newChild);
                }}
              />
            </div>
          )}
        </FieldArray>
      )}
    </div>
  );
}

export default SubChapter;
